import { queryClient } from '../api/network/queryClient';
import { getSignatureQueryConfig } from '../api/queries/signature';
import { getBrandStylesheetQueryConfig, getGlobalStylesheetQueryConfig } from '../api/queries/stylesheet';
import { getTicketQueryConfig } from '../api/queries/ticket';
import { initializeSentryTags } from '../components/Analytics/SentryHelpers';
import { SelfServiceError } from '../helpers/Errors';
import Language from '../helpers/Language';
import { Signature } from '../interfaces/Signature';
import { MultiLingualStylesheet, Stylesheet } from '../interfaces/Stylesheet';
import { Ticket } from '../interfaces/Ticket';
import { identifyPosthog, setAppointmentId } from './Analytics';
import { initializeChat } from './ChatService';
import { knownErrorStatuses, SupportedErrorTypes } from './ErrorService';
import FAQService from './FAQService';
import { isInMaintenanceMode } from './MaintenanceService';
import { getActiveStylesheet } from './StylesheetService';

export interface SignatureOutletContext {
  signature: Signature;
}

export interface TicketOutletContext {
  activeStylesheet: Stylesheet;
  brandStylesheet: MultiLingualStylesheet | undefined;
  globalStylesheet: MultiLingualStylesheet;
  ticket: Ticket;
}

export const ticketOutletLoader = async (hashId: string): Promise<TicketOutletContext> => {
  initializeSentryTags(hashId);
  identifyPosthog(hashId);

  const globalStylesheet = await queryClient
    .fetchQuery(getGlobalStylesheetQueryConfig())
    .catch(() => {
      throw new SelfServiceError({
        reason: SupportedErrorTypes.stylesheetNotFound,
      });
    });

  if (isInMaintenanceMode(globalStylesheet)) {
    throw new SelfServiceError({
      reason: SupportedErrorTypes.maintenance,
      stylesheet: globalStylesheet,
    });
  }

  const ticket = await queryClient
    .fetchQuery(getTicketQueryConfig(hashId))
    .catch(() => {
      throw new SelfServiceError({
        reason: SupportedErrorTypes.ticketNotFound,
        stylesheet: globalStylesheet,
      });
    });

  setAppointmentId(ticket.workOrder.appointment.id);

  const brandStylesheet = ticket
    ? await queryClient.fetchQuery(getBrandStylesheetQueryConfig(ticket.workOrder.workTypeId)).catch(() => undefined)
    : undefined;

  if (brandStylesheet && isInMaintenanceMode(brandStylesheet)) {
    throw new SelfServiceError({
      reason: SupportedErrorTypes.maintenance,
      stylesheet: brandStylesheet,
      ticket,
    });
  }

  if (brandStylesheet) {
    initializeChat(ticket, brandStylesheet[Language.getLanguage()]);
  }

  FAQService.prefetchFAQ(ticket.hashId);

  if (knownErrorStatuses[ticket.workOrder.status]) {
    throw new SelfServiceError({
      reason: knownErrorStatuses[ticket.workOrder.status],
      stylesheet: brandStylesheet ?? globalStylesheet,
      ticket,
    });
  }

  return {
    ticket,
    activeStylesheet: getActiveStylesheet(globalStylesheet, brandStylesheet, Language.getLanguage()),
    globalStylesheet,
    brandStylesheet,
  };
};


export const signatureOutletLoader = async (hashId: string): Promise<SignatureOutletContext> => {
  const signature = await queryClient.fetchQuery(getSignatureQueryConfig(hashId)).catch(() => undefined);

  if (!signature) {
    throw new SelfServiceError({
      reason: SupportedErrorTypes.noSignatureFound,
    });
  }

  return {
    signature,
  };
};
